import { EntityType } from '@frontend/common';
import { DefaultBadge, ObjectBadge } from '@frontend/components/badge';
import { Spinner } from '@frontend/components/common';
import { CommonMessage } from '@frontend/lang';
import { ProductType } from '@frontend/product/types';
import { LuCircleOff } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import PlusMinusNumberInput from '../../../components/plus-minus-number-input/plus-minus-number-input.component';
import { BudgetQuantity } from '../../components/budget-quantity/budget-quantity.component';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { ProductCard } from '../browse-products-by-categories/product-card/product-card.component';
import { useSelectQuantity } from './select-quantity.controller';

export const SelectQuantity = () => {
    const viewProps = useSelectQuantity();

    if (viewProps.isLoading) {
        return (
            <WizardScreenContainer
                footer={{ onBack: viewProps.navigateBack }}
                header={{ title: CommonMessage.WIZARD.SHOP.DEFAULT, onBack: viewProps.navigateBack }}>
                <div className='flex h-full w-full items-center justify-center py-12'>
                    <Spinner />
                </div>
            </WizardScreenContainer>
        );
    }

    if (!viewProps.product) {
        return (
            <WizardScreenContainer
                footer={{ onBack: viewProps.navigateBack }}
                header={{ title: CommonMessage.WIZARD.SHOP.DEFAULT, onBack: viewProps.navigateBack }}>
                <div className='flex h-full w-full items-center justify-center py-12'>
                    <p>
                        <FormattedMessage
                            id='wizard.select-quantity.product-not-found'
                            defaultMessage='No product selected'
                            description='Error message when the quantity screen is opened without a product'
                        />
                    </p>
                </div>
            </WizardScreenContainer>
        );
    }

    return (
        <WizardScreenContainer
            footer={{
                onBack: viewProps.navigateBack,
                onSubmit: viewProps.confirmQuantitySelection,
                submitLabel: viewProps.isOrderFallback ? CommonMessage.BUTTONS.CHOOSE_DELIVERY_LOCATION : CommonMessage.BUTTONS.CONTINUE,
                submitDisabled: !viewProps.canConfirm || viewProps.isCheckingOrderPermission || (viewProps.isOrderFallback && !viewProps.hasOrderFallbackStep)
            }}
            header={{ title: CommonMessage.WIZARD.SHOP.DEFAULT, onBack: viewProps.navigateBack }}>
            <div className='flex w-full flex-col items-start gap-6'>
                {/* Product Info */}
                <div className='flex w-full flex-col items-start gap-3'>
                    <div className='text-foreground-default flex flex-col justify-center text-base font-medium'>
                        <p>{CommonMessage.OBJECTS.PRODUCT.DEFAULT}</p>
                    </div>
                    <ProductCard
                        product={viewProps.product}
                        variant='attributes'
                    />
                </div>

                {/* Message when product not in stock at current spot */}
                {!viewProps.isRecycleMode &&
                    !viewProps.isOrderFallback &&
                    !viewProps.stockInfo.hasStockInCurrentSpot &&
                    viewProps.stockInfo.hasStockInOtherSpots && (
                        <div className='bg-background-surface outline-border-default flex w-full flex-col items-center gap-2 rounded-xl border p-4 outline -outline-offset-1'>
                            <div className='flex w-full items-center gap-3'>
                                <LuCircleOff className='text-foreground-default h-5 w-5' />
                                <div className='text-foreground-default text-base font-medium'>
                                    {CommonMessage.ERRORS.NOT_IN_STOCK}. {CommonMessage.ERRORS.PRODUCT_AVAILABLE_IN_FOLLOWING_LOCATIONS}
                                </div>
                            </div>
                            <div className='flex w-full flex-wrap justify-start gap-2'>
                                {viewProps.stockInfo.otherSpotIds.map((spotId) => (
                                    <ObjectBadge
                                        key={spotId}
                                        id={spotId}
                                        entityType={EntityType.SPOT}
                                        disableClick
                                        styles={{ icon: 'font-semibold size-4' }}
                                        className='outline-border-default flex h-6 items-center gap-1 font-semibold outline-1'
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                {!viewProps.isRecycleMode && !viewProps.isOrderFallback && viewProps.product.type === ProductType.EQUIPMENT && !viewProps.hasCertification && (
                    <div className='bg-background-surface outline-border-default flex w-full items-center gap-3 rounded-xl border p-4 outline -outline-offset-1'>
                        <LuCircleOff className='text-foreground-default h-5 w-5' />
                        <div className='text-foreground-default text-base font-medium'>{CommonMessage.ERRORS.NOT_CERTIFIED}</div>
                    </div>
                )}

                {/* Quantity Selector - Only show when stock is available */}
                {!viewProps.isRecycleMode &&
                    (viewProps.isOrderFallback || (viewProps.stockInfo.hasStockInCurrentSpot && viewProps.hasCertification)) &&
                    viewProps.totalMax > 0 && (
                        <div className='flex w-full flex-col items-start gap-3'>
                            <div className='text-foreground-default flex flex-col justify-center text-base font-medium'>
                                <p>{CommonMessage.OBJECTS.COMMON_FIELDS.AMOUNT}</p>
                            </div>
                            <PlusMinusNumberInput
                                min={1}
                                max={viewProps.totalMax}
                                value={viewProps.amount}
                                onChange={viewProps.changeAmount}
                            />
                            {/* Show availability info */}
                            <div className='text-foreground-default flex w-full justify-start gap-1 text-sm font-medium'>
                                {viewProps.maxBudget && Object.keys(viewProps.maxBudget.budgets).length > 0 && (
                                    <DefaultBadge
                                        styles={{ container: { color: 'bg-brand-muted' } }}
                                        label={
                                            <div className='flex gap-1'>
                                                <p>{CommonMessage.OBJECTS.BUDGET.DEFAULT}:</p>
                                                <p>
                                                    <BudgetQuantity quantity={viewProps.maxBudget.quantity} />
                                                </p>
                                            </div>
                                        }
                                    />
                                )}

                                {!viewProps.isOrderFallback && (
                                    <DefaultBadge
                                        styles={{ container: { color: 'bg-brand-muted' } }}
                                        label={
                                            <div className='flex gap-1'>
                                                <p>{CommonMessage.OBJECTS.STOCK.DEFAULT}:</p>
                                                <p>{viewProps.maxStock}</p>
                                            </div>
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    )}
            </div>
        </WizardScreenContainer>
    );
};
